import React, { useRef, useEffect, useCallback } from 'react';
import PostItem from './post-item';
import { PostItemSkeleton } from './post-item-skeleton';
import { EmptyScreen } from '@ui/components/moleculas';
import { ContextUserType } from '@ui/constants/context';
import { IMessageState } from '@ui/components/organisms/message/message.typings';

interface PostListSegmentProps {
  isLoading: boolean;
  initialLoading: boolean;
  messageState: IMessageState;
  userType: ContextUserType;
  isDynamicRoute?: boolean;
  emptyState: {
    title: string;
    message: string;
    actionButton?: {
      label: string;
      onClick: () => void;
    };
  };
  showEmptyScreen: boolean;
  nextPage: () => void;
  moderators_ids: string[];
}

const MemoizedPostItem = React.memo(PostItem);

const PostListSegment = (props: PostListSegmentProps) => {
  const loadingRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        props.messageState.hasMorePages &&
        !props.messageState.isFetching
      ) {
        props.nextPage();
      }
    },
    [props.messageState.hasMorePages, props.messageState.isFetching, props.nextPage],
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '100px',
      threshold: 0.1,
    });

    if (loadingRef.current) {
      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection]);

  const renderPinnedMessages = () => {
    if (
      props.messageState.isFetching ||
      props.isLoading ||
      props.initialLoading ||
      !props.messageState.pinned_messages?.length
    ) {
      return null;
    }

    return (
      <div className="mb-2">
        <div className="space-y-2 w-full">
          {props.messageState.pinned_messages?.map((post) => (
            <div key={post.id} className="min-w-[96%] flex-grow h-fit">
              <MemoizedPostItem
                key={post.id}
                post={post}
                userType={props.userType}
                userId={props.messageState.user_id}
                creator_id={post.creator_id}
                communityPostRoute={props.messageState.communityPostRoute}
                query={props.messageState.query}
                contentType="post"
                isDynamicRoute={props.isDynamicRoute}
                communityDetails={props.messageState.community_details}
                moderators_ids={props.moderators_ids}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLoadingState = () => {
    if (!props.isLoading && !props.initialLoading && !props.messageState.isFetching) {
      return null;
    }

    return (
      <div className="p-3 md:p-0">
        <PostItemSkeleton />
      </div>
    );
  };

  const renderMessages = () => {
    if (!props.messageState.messages.length) {
      return null;
    }

    return (
      <div className="pb-10 space-y-2" key={props.messageState.query.ordering}>
        {props.messageState.messages.map((post) => (
          <MemoizedPostItem
            key={post.id}
            post={post}
            userType={props.userType}
            userId={props.messageState.user_id}
            creator_id={post.creator_id}
            communityPostRoute={props.messageState.communityPostRoute}
            query={props.messageState.query}
            contentType="post"
            isDynamicRoute={props.isDynamicRoute}
            communityDetails={props.messageState.community_details}
            moderators_ids={props.moderators_ids}
          />
        ))}

        {props.messageState.hasMorePages && (
          <div ref={loadingRef} className="my-2">
            <PostItemSkeleton />
          </div>
        )}
      </div>
    );
  };

  const renderEmptyState = () => {
    if (
      props.messageState.isFetching ||
      !props.showEmptyScreen ||
      props.initialLoading ||
      props.messageState.pinned_messages?.length
    ) {
      return null;
    }

    const { actionButton, ...emptyStateProps } = props.emptyState;

    return (
      <EmptyScreen
        {...emptyStateProps}
        button={actionButton?.label}
        onClick={actionButton?.onClick}
        className="mx-auto h-fit w-full max-w-sm bg-transparent lg:bg-transparent"
      />
    );
  };

  return (
    <>
      {renderPinnedMessages()}
      {renderLoadingState()}
      {renderMessages()}
      {renderEmptyState()}
    </>
  );
};

export default PostListSegment;
